import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdt, inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, MessageCircle, Stethoscope, RefreshCw, PhoneCall } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  OVERDUE: "bg-rose-100 text-rose-700", DUE: "bg-amber-100 text-amber-700",
  APPROACHING: "bg-sky-100 text-sky-700", PRELIMINARY: "bg-slate-100 text-slate-600",
  RECENTLY_RETURNED: "bg-emerald-100 text-emerald-700",
};
const OUTCOMES = ["CONTACTED", "NO_ANSWER", "WHATSAPP_OPENED", "BOOKED", "NOT_INTERESTED",
  "FOLLOW_UP_LATER", "INVALID_NUMBER", "NO_RESPONSE", "CALL_BACK", "WRONG_NUMBER", "OTHER"];

function Badge({ status }) {
  return <span data-testid={`status-${status}`} className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>{status?.replace(/_/g, " ")}</span>;
}

function waLink(m) { const d = (m || "").replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}`; }

export default function FollowUps() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("returns");
  const [logFor, setLogFor] = useState(null);
  const vetOn = !!(user?.tenant?.feature_flags || {}).vet;

  const { data: dash, isFetching: dl } = useQuery({ queryKey: ["return-dash"], queryFn: async () => (await api.get("/return-dashboard")).data });
  const { data: fus } = useQuery({ queryKey: ["followups"], queryFn: async () => (await api.get("/followups")).data });
  const { data: conv } = useQuery({ queryKey: ["fu-conversion"], queryFn: async () => (await api.get("/followups/conversion")).data });
  const { data: vet } = useQuery({ queryKey: ["vet-followups"], enabled: vetOn, queryFn: async () => (await api.get("/vet-followups")).data });

  const createFromReturn = async (row) => {
    try {
      await api.post("/followups", { customer_id: row.customer_id, source: "return_pattern", reason: `Return ${row.status}` });
      toast.success("Follow-up created"); qc.invalidateQueries({ queryKey: ["followups"] }); qc.invalidateQueries({ queryKey: ["fu-conversion"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const completeVet = async (id) => {
    try { await api.patch(`/vet-followups/${id}/complete`, { status: "completed" }); toast.success("Completed"); qc.invalidateQueries({ queryKey: ["vet-followups"] }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const TABS = [["returns", "Due to Return"], ["followups", "Follow-ups"], ...(vetOn ? [["vet", "Vet Follow-ups"]] : [])];

  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="Data-driven return patterns & follow-up conversion — describes recorded history, not predictions" />

      <div className="flex gap-1.5 mb-5 border-b border-slate-200">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "returns" && (
        <div data-testid="returns-panel">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {["OVERDUE", "DUE", "APPROACHING", "RECENTLY_RETURNED", "PRELIMINARY"].map((k) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`count-${k}`}>
                <div className="text-2xl font-bold text-slate-900">{dash?.counts?.[k] ?? 0}</div>
                <div className="text-xs text-slate-500">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800">Customers due for return</h3>
              <Button size="sm" variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["return-dash"] })}><RefreshCw className={`h-4 w-4 ${dl ? "animate-spin" : ""}`} /></Button>
            </div>
            <div className="divide-y divide-slate-100 max-h-[560px] overflow-auto" data-testid="returns-list">
              {(dash?.items || []).length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No customers with a return pattern yet.</p>}
              {(dash?.items || []).map((r) => (
                <div key={r.customer_id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`return-row-${r.customer_id}`}>
                  <div className="min-w-[140px] flex-1">
                    <div className="font-medium text-slate-900">{r.customer_name || "—"}</div>
                    <div className="text-xs text-slate-400">{r.mobile}</div>
                  </div>
                  <Badge status={r.status} />
                  <div className="text-xs text-slate-500 min-w-[150px]">~{r.typical_interval_days ?? "—"}d interval · last {r.days_since_last}d ago</div>
                  <div className="text-xs text-slate-500 min-w-[120px]">expected {r.expected_return || "—"}</div>
                  <div className="flex gap-1.5">
                    <a href={`tel:${r.mobile}`} data-testid={`call-${r.customer_id}`}><Button size="sm" variant="outline" className="gap-1"><Phone className="h-3.5 w-3.5" />Call</Button></a>
                    <a href={waLink(r.mobile)} target="_blank" rel="noreferrer" data-testid={`wa-${r.customer_id}`}><Button size="sm" variant="outline" className="gap-1"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</Button></a>
                    <Button size="sm" onClick={() => createFromReturn(r)} data-testid={`mkfu-${r.customer_id}`} className="bg-slate-900 text-white">Create follow-up</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "followups" && (
        <div data-testid="followups-panel">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {[["total_followups", "Follow-ups"], ["contacted", "Contacted"], ["booked", "Booked"], ["completed", "Completed"], ["revenue_attributed", "Revenue"]].map(([k, l]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`conv-${k}`}>
                <div className="text-2xl font-bold text-slate-900">{k === "revenue_attributed" ? inr(conv?.[k] || 0) : (conv?.[k] ?? 0)}</div>
                <div className="text-xs text-slate-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 mb-4 text-xs text-slate-500" data-testid="conv-rates">
            Contacted → Booking: <b className="text-slate-800">{conv?.contacted_to_booking_rate ?? 0}%</b> &nbsp;·&nbsp; Booking → Completed: <b className="text-slate-800">{conv?.booking_to_completed_rate ?? 0}%</b>. Revenue attributed only from actual completed-booking invoices.
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="divide-y divide-slate-100 max-h-[520px] overflow-auto" data-testid="followups-list">
              {(fus?.items || []).length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No follow-ups yet. Create one from the “Due to Return” tab.</p>}
              {(fus?.items || []).map((f) => (
                <div key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`fu-row-${f.id}`}>
                  <div className="min-w-[140px] flex-1">
                    <div className="font-medium text-slate-900">{f.customer_name}</div>
                    <div className="text-xs text-slate-400">{f.source} · {f.reason || "—"}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${f.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{f.status}</span>
                  {f.contacted && <span className="text-xs text-emerald-600">contacted</span>}
                  {f.booked && <span className="text-xs text-sky-600">booked</span>}
                  {f.latest_outcome && <span className="text-xs text-slate-400">{f.latest_outcome}</span>}
                  <div className="flex gap-1.5">
                    <a href={`tel:${f.mobile}`}><Button size="sm" variant="outline" className="gap-1"><Phone className="h-3.5 w-3.5" />Call</Button></a>
                    <a href={waLink(f.mobile)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</Button></a>
                    <Button size="sm" onClick={() => setLogFor(f)} data-testid={`log-${f.id}`} className="bg-slate-900 text-white">Log outcome</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "vet" && vetOn && (
        <div data-testid="vet-panel">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {["due_today", "upcoming", "overdue", "completed", "missed"].map((k) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`vet-count-${k}`}>
                <div className="text-2xl font-bold text-slate-900">{vet?.counts?.[k] ?? 0}</div>
                <div className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="divide-y divide-slate-100 max-h-[520px] overflow-auto" data-testid="vet-list">
              {(vet?.items || []).length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No veterinary follow-ups.</p>}
              {(vet?.items || []).map((v) => (
                <div key={v.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`vetfu-row-${v.id}`}>
                  <Stethoscope className="h-4 w-4 text-indigo-500" />
                  <div className="min-w-[140px] flex-1">
                    <div className="font-medium text-slate-900">{v.pet_name} <span className="text-xs text-slate-400">· {v.reason}</span></div>
                    <div className="text-xs text-slate-400">{v.clinical_restricted ? "clinical note restricted" : (v.notes || "")}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${v.bucket === "overdue" ? "bg-rose-100 text-rose-700" : v.bucket === "due_today" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{v.bucket?.replace(/_/g, " ")}</span>
                  <div className="text-xs text-slate-500 min-w-[110px]">due {v.due_date}</div>
                  <div className="text-xs text-slate-500 min-w-[120px]">{v.responsible_staff_name || "unassigned"}</div>
                  {!["completed", "missed", "cancelled"].includes(v.status) && (
                    <Button size="sm" onClick={() => completeVet(v.id)} data-testid={`vet-complete-${v.id}`} className="bg-slate-900 text-white">Complete</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {logFor && <LogDialog fu={logFor} onClose={() => setLogFor(null)} onDone={() => { setLogFor(null); qc.invalidateQueries({ queryKey: ["followups"] }); qc.invalidateQueries({ queryKey: ["fu-conversion"] }); }} />}
    </div>
  );
}

function LogDialog({ fu, onClose, onDone }) {
  const [form, setForm] = useState({ channel: "call", outcome: "CONTACTED", notes: "", next_followup_date: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/followups/${fu.id}/log`, form); toast.success("Outcome logged"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="log-dialog">
        <DialogHeader><DialogTitle className="font-display">Log follow-up — {fu.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500 mb-1 block">Channel</Label>
            <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
              <SelectTrigger className="bg-white" data-testid="log-channel"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["call", "whatsapp", "sms", "email", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Outcome</Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger className="bg-white" data-testid="log-outcome"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-60">{OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white" data-testid="log-notes" /></div>
          <div><Label className="text-xs text-slate-500 mb-1 block">Next follow-up date</Label>
            <Input type="date" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} className="bg-white" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="log-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save outcome"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
